import React, { useEffect, useState } from "react";
import PublicLayout from "../../layouts/PublicLayout";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import DataTable from "../../components/DataTable";
import { publicInstituteList } from "../../api/onlineAdmissionApi";
import { view, invoiceList, moneyReceipt } from "../../api/studentPortalApi";
import { previewPayableFees, createSession } from "../../api/onlineFeePaymentApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Public, unauthenticated — digicampusbd's {@code StudentPortalLoginController}
 * "login" (really a customStudentId+institute+year lookup, no password) +
 * fee-invoice/money-receipt view + online-payment. Stateless here (the
 * looked-up student stays in this component's own state) rather than the
 * old app's HttpSession. See MIGRATION.md Phase 10 notes.
 */
export default function StudentPortalPage() {
  const [student, setStudent] = useState(null);
  const [instituteId, setInstituteId] = useState(null);

  return (
    <PublicLayout title="Student Portal">
      <div className="public-card">
        {!student ? (
          <LoginForm onLoggedIn={(data, iid) => { setStudent(data); setInstituteId(iid); }} />
        ) : (
          <StudentDashboard student={student} instituteId={instituteId} onLogout={() => setStudent(null)} />
        )}
      </div>
    </PublicLayout>
  );
}

function LoginForm({ onLoggedIn }) {
  const [institutes, setInstitutes] = useState([]);
  const [instituteId, setInstituteId] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [customStudentId, setCustomStudentId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    publicInstituteList().then(setInstitutes);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await view(customStudentId, instituteId, academicYear);
      onLoggedIn(data, Number(instituteId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "No active student found with this ID.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="user-form">
      <h2>Student Portal Login</h2>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <label>Institute</label>
      <select value={instituteId} onChange={(e) => setInstituteId(e.target.value)} required>
        <option value="">Select institute</option>
        {institutes.map((i) => (
          <option key={i.id} value={i.id}>
            {i.name}
          </option>
        ))}
      </select>
      <label>Academic Year</label>
      <input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} placeholder="e.g. 2025" required />
      <label>Student ID</label>
      <input value={customStudentId} onChange={(e) => setCustomStudentId(e.target.value)} required />
      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? "Checking..." : "View My Account"}
        </button>
      </div>
    </form>
  );
}

function StudentDashboard({ student, instituteId, onLogout }) {
  const [tab, setTab] = useState("profile");
  const s = student.student;

  return (
    <>
      <div className="page-header">
        <div>
          <h2>{student.instituteName}</h2>
          <p style={{ margin: 0, color: "#6b7280" }}>{student.instituteAddress}</p>
        </div>
        <button type="button" className="btn-secondary" onClick={onLogout}>
          Logout
        </button>
      </div>

      <Tabs
        tabs={[
          { key: "profile", label: "Profile" },
          { key: "invoices", label: "Fee Invoices" },
          { key: "pay", label: "Pay Online" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "profile" && (
        <div className="table-scroll" style={{ marginTop: 12 }}>
        <table className="attendance-table">
          <tbody>
            <tr>
              <th>Name</th>
              <td>{s.studentName}</td>
            </tr>
            <tr>
              <th>Student ID</th>
              <td>{s.customStudentId}</td>
            </tr>
            <tr>
              <th>Class</th>
              <td>{s.classConfigName}</td>
            </tr>
            <tr>
              <th>Roll</th>
              <td>{s.studentRoll}</td>
            </tr>
            <tr>
              <th>Father</th>
              <td>{s.fatherName}</td>
            </tr>
            <tr>
              <th>Mother</th>
              <td>{s.motherName}</td>
            </tr>
            <tr>
              <th>Guardian Mobile</th>
              <td>{s.guardianMobile}</td>
            </tr>
          </tbody>
        </table>
        </div>
      )}

      {tab === "invoices" && <InvoicesTab identificationId={s.identificationId} instituteId={instituteId} />}
      {tab === "pay" && <PayOnlineTab customStudentId={s.customStudentId} instituteId={instituteId} academicYear={s.academicYear} />}
    </>
  );
}

function InvoicesTab({ identificationId, instituteId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    invoiceList(identificationId, instituteId)
      .then(setInvoices)
      .catch(() => setError("Failed to load invoices."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewReceipt = async (invoiceId) => {
    setError("");
    try {
      const data = await moneyReceipt(invoiceId, instituteId);
      setReceipt(data);
    } catch (err) {
      setError("Failed to load receipt.");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <DataTable
        columns={[
          { key: "invoiceId", label: "Invoice #" },
          { key: "academicYear", label: "Year" },
          { key: "paymentDate", label: "Date" },
          { key: "payableAmount", label: "Payable" },
          { key: "paidAmount", label: "Paid" },
          { key: "dueAmount", label: "Due" },
        ]}
        rows={invoices}
        emptyMessage="No fee invoices found."
        actions={(row) => (
          <button type="button" className="btn-link" onClick={() => viewReceipt(row.invoiceId)}>
            View Receipt
          </button>
        )}
      />

      {receipt && (
        <div className="public-card" style={{ marginTop: 16, border: "1px solid #e5e7eb" }}>
          <div className="page-header">
            <h3>Money Receipt #{receipt.invoiceId}</h3>
            <button type="button" className="btn-link" onClick={() => setReceipt(null)}>
              Close
            </button>
          </div>
          <p>
            {receipt.studentName} (Roll {receipt.roll}) — {receipt.className} {receipt.shiftName} {receipt.sectionName}
          </p>
          <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Fee Head</th>
                <th>Sub Head</th>
                <th>Payable</th>
                <th>Waiver</th>
                <th>Fine</th>
                <th>Paid</th>
              </tr>
            </thead>
            <tbody>
              {receipt.lines.map((l, idx) => (
                <tr key={idx}>
                  <td>{l.feeHeadName}</td>
                  <td>{l.feeSubHeadName}</td>
                  <td>{l.payable}</td>
                  <td>{l.waiver}</td>
                  <td>{l.fine}</td>
                  <td>{l.paid}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p>
            <strong>Total Paid: {receipt.totalPaid}</strong> ({receipt.paidInWord})
          </p>
          <p style={{ fontSize: "0.85rem", color: "#6b7280" }}>
            Paid via {receipt.username} on {receipt.paymentDate}
          </p>
        </div>
      )}
    </>
  );
}

function PayOnlineTab({ customStudentId, instituteId, academicYear }) {
  const [year, setYear] = useState(academicYear || "");
  const [months, setMonths] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const toggleMonth = (m) => setMonths((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const handlePreview = async () => {
    setError("");
    setPreview(null);
    if (months.length === 0) {
      setError("Select at least one month.");
      return;
    }
    setLoading(true);
    try {
      const data = await previewPayableFees({ customStudentId, instituteId, academicYear: year, months });
      setPreview(data);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load payable fees.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!preview || preview.payableLines.length === 0) return;
    if (
      !window.confirm(
        `This will redirect you to the SSLCommerz payment gateway to pay ${preview.totalPayable} Taka. Continue?`
      )
    ) {
      return;
    }
    setRedirecting(true);
    setError("");
    try {
      const session = await createSession({ identificationId: preview.identificationId, lines: preview.payableLines });
      window.location.href = session.gatewayPageUrl;
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to start payment session.");
      setRedirecting(false);
    }
  };

  return (
    <>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <div className="attendance-filters">
        <label>
          Year
          <input value={year} onChange={(e) => setYear(e.target.value)} style={{ width: 100 }} />
        </label>
      </div>
      <div style={{ margin: "12px 0" }}>
        {MONTHS.map((m) => (
          <label key={m} className="checkbox-label" style={{ display: "inline-flex", marginRight: 10 }}>
            <input type="checkbox" checked={months.includes(m)} onChange={() => toggleMonth(m)} /> {m}
          </label>
        ))}
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={handlePreview} disabled={loading}>
          {loading ? "Loading..." : "Show Payable Fees"}
        </button>
      </div>

      {preview && (
        <div style={{ marginTop: 16 }}>
          {preview.payableLines.length === 0 ? (
            <Alert type="success">No fees are due for the selected period.</Alert>
          ) : (
            <>
              <div className="table-scroll">
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Fee Head</th>
                    <th>Sub Head</th>
                    <th>Amount</th>
                    <th>Fine</th>
                    <th>Waiver</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.payableLines.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.feeHeadId}</td>
                      <td>{l.feeSubHeadId}</td>
                      <td>{l.feeAmount}</td>
                      <td>{l.fineAmount}</td>
                      <td>{l.waiverAmount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <p>
                <strong>Total Payable: {preview.totalPayable} Taka</strong>
              </p>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={handlePayNow} disabled={redirecting}>
                  {redirecting ? "Redirecting..." : "Pay Online"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
